import type { AppProps } from 'next/app';
import { useEffect, useRef } from 'react';
import { Rethink_Sans } from 'next/font/google';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useRouter } from 'next/router';
// import { Provider, ErrorBoundary } from '@rollbar/react'; // <-- Provider imports 'rollbar' for us
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';

import 'styles/styles.css';

import { configureAmplify } from 'lib/amplify';
import GlobalStateProvider from 'contexts/global-state-context';
import WordPressProvider from 'contexts/wordpress-provider';
import BrazeProvider from 'contexts/braze-context';

import AppHead from 'components/head';
import { EventsTrackerProvider } from 'contexts/eventsTracker';
import { POSTHOG_API_HOST, POSTHOG_API_KEY } from 'lib/posthog';
import { getPosthogBootstrapCookie } from 'lib/cookies/posthog-cookie';

const PreviewBar = dynamic(() => import('components/preview-bar'));

declare global {
  interface Window {
    routeTimeout?: NodeJS.Timeout;
  }
}

const rethink = Rethink_Sans({
  subsets: ['latin'],
  variable: '--font-rethink',
  display: 'swap',
});

// Amplify initialization (Cognito auth for login widget)
if (typeof window !== 'undefined') {
  configureAmplify();
}

// Posthog initialization
if (typeof window !== 'undefined') {
  // Read distinctID from bootstrap cookie (set by middleware)
  const bootstrapCookie = getPosthogBootstrapCookie();
  let cookieData: { distinctID?: string } = {};

  if (bootstrapCookie) {
    try {
      cookieData = JSON.parse(bootstrapCookie);
    } catch {
      //
    }
  }

  // Read featureFlags from __NEXT_DATA__ (set by getServerSideProps)
  // Available in the HTML before any JS bundle loads
  const nextData = (window as any).__NEXT_DATA__;
  const posthogBootstrap = nextData?.props?.pageProps?.posthogBootstrap;

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_API_HOST,
    disable_web_experiments: true,
    autocapture: true,
    bootstrap: {
      distinctID: posthogBootstrap?.distinctID || cookieData.distinctID,
      featureFlags: posthogBootstrap?.featureFlags || {},
    },
    advanced_disable_feature_flags_on_first_load: true,
    loaded: posthog => {
      if (process.env.NODE_ENV === 'development') posthog.debug();
    },
  });
}

const App: React.FC<AppProps<any>> = ({ Component, pageProps }) => {
  const router = useRouter();

  const maxPercentage = useRef(0);
  const maxPixels = useRef(0);

  useEffect(() => {
    function handleScroll() {
      const lastPercentage = Math.min(
        1,
        (window.innerHeight + window.pageYOffset) / document.body.offsetHeight,
      );
      const lastPixels = window.innerHeight + window.pageYOffset;
      if (lastPercentage > maxPercentage.current) {
        maxPercentage.current = lastPercentage;
      }
      if (lastPixels > maxPixels.current) {
        maxPixels.current = lastPixels;
      }
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    let hasFired = false;

    const handleStart = (url, { shallow }) => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      if (shallow) {
        return;
      }

      window.routeTimeout = setTimeout(() => {
        if (url !== window.location.pathname) {
          window.location.assign(url);
        }
      }, 150);
    };

    const handleStop = () => {
      window?.routeTimeout && clearTimeout(window?.routeTimeout);
    };

    const handleComplete = () => {
      posthog?.capture('$pageview');
      handleStop();
    };

    const handlePageLeave = () => {
      if (hasFired) return;
      hasFired = true;
      posthog.capture(
        'page_left',
        {
          'max scroll percentage': maxPercentage.current,
          'max scroll pixels': maxPixels.current,
          'last scroll percentage': Math.min(
            1,
            (window.innerHeight + window.pageYOffset) /
              document.body.offsetHeight,
          ),
          'last scroll pixels': window.innerHeight + window.pageYOffset,
          scrolled: maxPixels.current > 0,
        },
        {
          transport: 'sendBeacon',
        },
      );
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleStop);
    window.addEventListener('pagehide', handlePageLeave);
    window.addEventListener('beforeunload', handlePageLeave, true);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleStop);
      window.removeEventListener('pagehide', handlePageLeave);
      window.removeEventListener('beforeunload', handlePageLeave);
    };
  }, []);

  return (
    <>
      <style jsx global>{`
        html {
          font-family: ${rethink.style.fontFamily};
        }
      `}</style>
      <AppHead webmasterMeta={pageProps?.seoSettings?.webmaster} />
      {process.env.NODE_ENV === 'production' ? (
        <Script
          id="leafwell-zaraz"
          type="text/javascript"
          src="https://zaraz.leafwell.com/cdn-cgi/zaraz/i.js"
        />
      ) : null}
      {process.env.NODE_ENV !== 'production' ? (
        <Script
          id="leafwell-trustpilot"
          type="text/javascript"
          src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
          async
        />
      ) : null}
      <div id="leafwell-container" className={`${rethink.variable}`}>
        {pageProps?.isPreview && <PreviewBar />}
        <PostHogProvider client={posthog}>
          {/* <Provider
          config={{
            accessToken: '59e5f45e087646fd8c7031e25a539344',
            environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
            // Change these 2 props to true after we've identified the Hubspot error
            captureUncaught: false,
            captureUnhandledRejections: false,
          }}
        >*/}
          {/* ErrorBoundary catches all React errors in the tree below and logs them to Rollbar 
          <ErrorBoundary> */}
          <WordPressProvider value={pageProps?.settings ?? {}}>
            <GlobalStateProvider>
              <BrazeProvider>
                <EventsTrackerProvider>
                  <Component {...pageProps} key={router.asPath} />
                </EventsTrackerProvider>
              </BrazeProvider>
            </GlobalStateProvider>
          </WordPressProvider>
          {/*</ErrorBoundary>
        </Provider> */}
        </PostHogProvider>
      </div>
    </>
  );
};

export default App;
